/**
 * Character Style Overrides Utilities
 * Functions for manipulating the charStyleOverrides array during text editing
 *
 * charStyleOverrides is an array where:
 * - index = character position in text
 * - value = style ID (e.g., "1:2") or 0 for base style
 */

/**
 * Handle Backspace key - removes the character BEFORE the cursor
 * @param {Array} overrides - Current charStyleOverrides array
 * @param {number} caretPosition - Current caret position (after the char to delete)
 * @returns {Array} New overrides array with element removed
 */
export function handleBackspace(overrides, caretPosition) {
  if (!overrides || overrides.length === 0) return overrides || [];
  if (caretPosition <= 0) return overrides;

  const result = [...overrides];
  result.splice(caretPosition - 1, 1);
  return result;
}

/**
 * Handle Delete key - removes the character AT the cursor
 * @param {Array} overrides - Current charStyleOverrides array
 * @param {number} caretPosition - Current caret position (at the char to delete)
 * @param {number} textLength - Total length of text
 * @returns {Array} New overrides array with element removed
 */
export function handleDelete(overrides, caretPosition, textLength) {
  if (!overrides || overrides.length === 0) return overrides || [];
  if (caretPosition >= textLength) return overrides;

  const result = [...overrides];
  result.splice(caretPosition, 1);
  return result;
}

/**
 * Insert style overrides for new characters (single char or multi-char like paste)
 * @param {Array} overrides - Current charStyleOverrides array
 * @param {number} position - Position to insert at
 * @param {number} count - Number of characters being inserted
 * @param {string|number} styleId - Style ID to apply (0 for base style, or style ID like "1:2")
 * @returns {Array} New overrides array with elements inserted
 */
export function insertOverrides(overrides, position, count, styleId = 0) {
  if (count <= 0) return overrides || [];

  const result = [...(overrides || [])];
  const toInsert = Array(count).fill(styleId);
  result.splice(position, 0, ...toInsert);
  return result;
}

/**
 * Delete multiple style overrides (e.g., when deleting a selection)
 * @param {Array} overrides - Current charStyleOverrides array
 * @param {number} position - Start position of deletion
 * @param {number} count - Number of characters being deleted
 * @returns {Array} New overrides array with elements removed
 */
export function deleteOverrides(overrides, position, count) {
  if (!overrides || overrides.length === 0) return overrides || [];
  if (count <= 0) return overrides;

  const result = [...overrides];
  result.splice(position, count);
  return result;
}

/**
 * Get the style ID at a specific position, with fallback to adjacent character's style
 * Useful for extending styles when typing new characters
 * @param {Array} overrides - Current charStyleOverrides array
 * @param {number} position - Position to check
 * @returns {string|number} Style ID or 0
 */
export function getStyleAtPosition(overrides, position) {
  if (!overrides || overrides.length === 0) return 0;

  // If position is within array, return that style
  if (position >= 0 && position < overrides.length) {
    return overrides[position] || 0;
  }

  // If at end, use last character's style
  if (position >= overrides.length && overrides.length > 0) {
    return overrides[overrides.length - 1] || 0;
  }

  return 0;
}
